/**
 * Position formatting. Everything on the chart reads in degrees, minutes and
 * decimal minutes (DMm), because that's how paper charts and most fitted
 * chartplotters work by default, and it's how RYA courses teach it. We do not
 * surface DMS or decimal degrees in the UI.
 *
 * Example: 50°47.832' N, 001°18.456' W
 *
 * The numeric inputs are [lon, lat] WGS84 degrees, matching GeoJSON.
 */
import type { PositionFormat } from '@/store/units';

export interface FormatPositionOptions {
  /** Number of decimal places on the minute value. Default 3 (i.e. ~1.8 m). */
  decimals?: number;
  /** True to omit the degree symbol etc. for a compact monospace readout. */
  compact?: boolean;
  /** Position format: DMm (default), DMS, or decimal degrees. */
  format?: PositionFormat;
}

/** Format a single coordinate (latitude or longitude), honouring the chosen format. */
export function formatLatLon(
  value: number,
  axis: 'lat' | 'lon',
  opts: FormatPositionOptions = {},
): string {
  const format = opts.format ?? 'dmm';
  const abs = Math.abs(value);
  // Latitude needs 2 leading digits, longitude 3 (UK conventions, matches the charts).
  const degPad = axis === 'lat' ? 2 : 3;
  const hemi = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';

  if (format === 'dd') {
    // Decimal degrees, e.g. 50.79720°N.
    const decimals = opts.decimals ?? 5;
    const degStr = abs.toFixed(decimals).padStart(degPad + 1 + decimals, '0');
    return opts.compact ? `${degStr}${hemi}` : `${degStr}°${hemi}`;
  }

  if (format === 'dms') {
    // Degrees, minutes, seconds, e.g. 50°47'50"N.
    const deg = Math.trunc(abs);
    const totalMin = (abs - deg) * 60;
    let min = Math.trunc(totalMin);
    let sec = Math.round((totalMin - min) * 60);
    if (sec === 60) { sec = 0; min += 1; }
    const degStr = String(deg).padStart(degPad, '0');
    const minStr = String(min).padStart(2, '0');
    const secStr = String(sec).padStart(2, '0');
    return opts.compact
      ? `${degStr} ${minStr} ${secStr}${hemi}`
      : `${degStr}°${minStr}'${secStr}"${hemi}`;
  }

  // DMm (default): degrees and decimal minutes.
  const decimals = opts.decimals ?? 3;
  const deg = Math.trunc(abs);
  const min = (abs - deg) * 60;
  const minPad = decimals === 0 ? 2 : 2 + 1 + decimals;
  const degStr = String(deg).padStart(degPad, '0');
  const minStr = min.toFixed(decimals).padStart(minPad, '0');
  return opts.compact ? `${degStr} ${minStr}${hemi}` : `${degStr}°${minStr}'${hemi}`;
}

/** Format [lon, lat] as "lat\nlon" (two lines) so it sits well in a HUD. */
export function formatPosition(
  position: [number, number] | null | undefined,
  opts: FormatPositionOptions = {},
): string {
  if (!position) return '—';
  const [lon, lat] = position;
  return `${formatLatLon(lat, 'lat', opts)}  ${formatLatLon(lon, 'lon', opts)}`;
}

/**
 * Parse a position string in DMm form back into [lon, lat]. Tolerant of the
 * common variations: degree symbol or space, apostrophe or minute-prime,
 * separator between lat and lon being a comma, slash, or newline.
 *
 * Examples accepted:
 *   "50 47.832 N 001 18.456 W"
 *   "50°47.832'N, 001°18.456'W"
 *   "50 47.832N 1 18.456W"
 *
 * Returns null if the string can't be parsed.
 */
export function parsePosition(input: string): [number, number] | null {
  if (!input) return null;
  // Normalise punctuation to plain spaces.
  const norm = input
    .replace(/[°º]/g, ' ')
    .replace(/['′]/g, ' ')
    .replace(/["″]/g, ' ')
    .replace(/[,/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  // Match two "D M.m H" groups, with H ∈ {N,S,E,W} in either order.
  const re = /(\d{1,3})\s+(\d{1,2}(?:\.\d+)?)\s*([NSEW])/g;
  const matches: { deg: number; min: number; hemi: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(norm))) {
    matches.push({ deg: parseInt(m[1], 10), min: parseFloat(m[2]), hemi: m[3] });
  }
  if (matches.length !== 2) return null;
  let lat: number | null = null;
  let lon: number | null = null;
  for (const x of matches) {
    const dec = x.deg + x.min / 60;
    if (x.hemi === 'N' || x.hemi === 'S') {
      lat = x.hemi === 'N' ? dec : -dec;
    } else {
      lon = x.hemi === 'E' ? dec : -dec;
    }
  }
  if (lat === null || lon === null) return null;
  return [lon, lat];
}

/**
 * Global bearing display reference. The app works in TRUE throughout (every
 * stored/computed bearing is degrees true); bearings are only converted to the
 * chosen reference at display time, here, so there's one place to keep right.
 *
 *   'true'     — show degrees true, suffix °T (default).
 *   'magnetic' — subtract the magnetic variation and suffix °M.
 *
 * Variation is East-positive, the usual convention: True = Magnetic + Variation,
 * so Magnetic = True − Variation. e.g. with 4°E variation a true bearing of 100°
 * shows as 096°M. A module-level mirror (kept in step by useBearingDisplayStore)
 * lets these pure formatters read the setting without every caller threading it.
 */
export type BearingMode = 'true' | 'magnetic';
let bearingMode: BearingMode = 'true';
let bearingVariationDeg = 0;

/** Set the display reference + variation (called by the store on change/init). */
export function setBearingDisplay(mode: BearingMode, variationDeg: number): void {
  bearingMode = mode === 'magnetic' ? 'magnetic' : 'true';
  bearingVariationDeg = Number.isFinite(variationDeg) ? variationDeg : 0;
}

/** The current display reference, for callers that need to branch on it. */
export function getBearingMode(): BearingMode {
  return bearingMode;
}

/** Convert a TRUE bearing to the current display reference (true or magnetic). */
export function toDisplayBearing(trueDeg: number): number {
  const t = ((trueDeg % 360) + 360) % 360;
  if (bearingMode === 'magnetic') return (((t - bearingVariationDeg) % 360) + 360) % 360;
  return t;
}

/** The current bearing suffix, "°T" or "°M". */
export function bearingSuffix(): string {
  return bearingMode === 'magnetic' ? '°M' : '°T';
}

/**
 * Format a bearing for display in the current reference. The input is a TRUE
 * bearing; it is converted (if the reference is magnetic) and suffixed °T/°M to
 * match, so a reader is never left guessing which north a bearing is measured from.
 */
export function formatBearing(degrees: number, decimals = 0): string {
  const shown = toDisplayBearing(degrees);
  return `${shown.toFixed(decimals).padStart(3 + (decimals ? decimals + 1 : 0), '0')}${bearingSuffix()}`;
}

/** Format a distance in nautical miles. */
export function formatDistance(nm: number, decimals = 2): string {
  return `${nm.toFixed(decimals)} nm`;
}

/**
 * Format a magnetic variation for display. The input is signed and East-positive
 * (the app's convention), so a negative value is West. We show it the way it
 * reads off a chart's compass rose — magnitude then hemisphere, e.g. 4°W or 3°E —
 * because that's the form students meet on RYA courses and paper charts. A value
 * of zero has no hemisphere, so it shows plainly as 0°.
 */
export function formatVariation(signedDeg: number, decimals = 0): string {
  const v = Number.isFinite(signedDeg) ? signedDeg : 0;
  const mag = Math.abs(v);
  const magStr = mag.toFixed(decimals);
  if (parseFloat(magStr) === 0) return `${magStr}°`;
  return `${magStr}°${v < 0 ? 'W' : 'E'}`;
}
